import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { CommissionsRepository } from '@app/domain/ports/tpv/commissions.repository';
import {
  CompanyCommissionsMatrix,
  CommissionsReadDto,
  MerchantCommissionsMarkup,
  UnitCommissionsMarkup,
} from '@app/data/dtos/tpv/commissions.dto';
import { tpvUrl } from '@app/data/tpv/tpv-api.config';
import { TpvEnvelope, unwrapTpv } from '@app/data/tpv/tpv-envelope';

/**
 * CRUD HTTP de comisiones (§1). Auth vía interceptores estándar. Los GET toleran
 * `404` (sin matriz configurada) devolviendo `null` en vez de romper la pantalla.
 */
@Injectable()
export class CommissionsRemoteDataSource extends CommissionsRepository {
  private readonly http = inject(HttpClient);

  // ── Empresa ────────────────────────────────────────────────────────────────
  override getCompanyCommissions(idCompany: string): Observable<CommissionsReadDto | null> {
    return this.http
      .get<TpvEnvelope<CommissionsReadDto> | CommissionsReadDto>(
        tpvUrl(`companies/${idCompany}/commissions/`),
      )
      .pipe(
        map((resp) => this.toReadOrNull(resp)),
        catchError(() => of(null)),
      );
  }

  override createCompanyCommissions(idCompany: string, body: CompanyCommissionsMatrix): Observable<void> {
    return this.http
      .post<TpvEnvelope<unknown>>(tpvUrl(`companies/${idCompany}/commissions/`), body)
      .pipe(map(() => void 0));
  }

  override updateCompanyCommissions(idCompany: string, body: Partial<CompanyCommissionsMatrix>): Observable<void> {
    return this.http
      .patch<TpvEnvelope<unknown>>(tpvUrl(`companies/${idCompany}/commissions/`), body)
      .pipe(map(() => void 0));
  }

  override deleteCompanyCommissions(idCompany: string): Observable<void> {
    return this.http
      .delete<TpvEnvelope<unknown>>(tpvUrl(`companies/${idCompany}/commissions/`))
      .pipe(map(() => void 0));
  }

  // ── Unidad ───────────────────────────────────────────────────────────────────
  override getUnitCommissions(idCompany: string, idUnit: string): Observable<CommissionsReadDto | null> {
    return this.http
      .get<TpvEnvelope<CommissionsReadDto> | CommissionsReadDto>(
        tpvUrl(`companies/${idCompany}/units/${idUnit}/commissions/`),
      )
      .pipe(
        map((resp) => this.toReadOrNull(resp)),
        catchError(() => of(null)),
      );
  }

  override updateUnitCommissions(idCompany: string, idUnit: string, body: UnitCommissionsMarkup): Observable<void> {
    return this.http
      .patch<TpvEnvelope<unknown>>(tpvUrl(`companies/${idCompany}/units/${idUnit}/commissions/`), body)
      .pipe(map(() => void 0));
  }

  // ── Comercio ───────────────────────────────────────────────────────────────
  override getMerchantCommissions(idMerchant: string): Observable<CommissionsReadDto | null> {
    return this.http
      .get<TpvEnvelope<CommissionsReadDto> | CommissionsReadDto>(
        tpvUrl(`merchants/${idMerchant}/commissions/`),
      )
      .pipe(
        map((resp) => this.toReadOrNull(resp)),
        catchError(() => of(null)),
      );
  }

  override updateMerchantCommissions(idMerchant: string, body: MerchantCommissionsMarkup): Observable<void> {
    return this.http
      .patch<TpvEnvelope<unknown>>(tpvUrl(`merchants/${idMerchant}/commissions/`), body)
      .pipe(map(() => void 0));
  }

  private toReadOrNull(resp: TpvEnvelope<CommissionsReadDto> | CommissionsReadDto): CommissionsReadDto | null {
    const raw = unwrapTpv(resp) as Record<string, unknown> | null | undefined;
    if (!raw || typeof raw !== 'object') return null;
    // El backend envuelve los campos reales junto a metadatos (source/updated_at/…):
    //  - Empresa (espejo local): bajo `matrix`.
    //  - Unidad/Comercio (markup live): bajo `markup`.
    // Extraemos el bloque interno para que el consumidor reciba los campos planos;
    // si no viene envuelto, se devuelve tal cual.
    const inner = (raw['matrix'] ?? raw['markup']) as Record<string, unknown> | undefined;
    const data = (inner && typeof inner === 'object' ? inner : raw) as CommissionsReadDto;
    if (data && typeof data === 'object' && Object.keys(data).length > 0) {
      return data;
    }
    return null;
  }
}
